/*
 * The account API's shapes (apps/account-api/src/services/profile.ts and routes/me.ts).
 * Responses are checked on arrival (client.ts) rather than trusted.
 */

/** `GET /api/me`: everything the account holds. */
export interface AccountProfile {
  id: string;
  givenName: string | null;
  surname: string | null;
  email: string | null;
  /** `YYYY-MM`. */
  birthMonth: string | null;
  /** From the birth month, conservatively. */
  age: number | null;
  /** When the account was created (ISO 8601). */
  memberSince: string | null;
  terms: { version: string | null; acceptedAt: string | null };
}

/** `PATCH /api/me`. */
export interface NameChanges {
  givenName?: string;
  surname?: string;
}

/** `DELETE /api/me`: 200 deleted, or 202 accepted and completing shortly. */
export type DeletionResult =
  { status: 'deleted'; deletedAt: string | null } | { status: 'pending' };

/** `GET /api/me/export`: the file to save. */
export interface ExportFile {
  blob: Blob;
  filename: string;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

/** The profile, or null when `value` is not one. */
export function parseProfile(value: unknown): AccountProfile | null {
  if (typeof value !== 'object' || value === null) return null;
  const body = value as Record<string, unknown>;
  const terms = body.terms;
  if (typeof terms !== 'object' || terms === null) return null;
  const { version, acceptedAt } = terms as Record<string, unknown>;
  const { id, givenName, surname, email, birthMonth, age, memberSince } = body;
  if (
    typeof id !== 'string' ||
    !isNullableString(givenName) ||
    !isNullableString(surname) ||
    !isNullableString(email) ||
    !isNullableString(birthMonth) ||
    !(age === null || (typeof age === 'number' && Number.isFinite(age))) ||
    !isNullableString(memberSince) ||
    !isNullableString(version) ||
    !isNullableString(acceptedAt)
  ) {
    return null;
  }
  return {
    id,
    givenName,
    surname,
    email,
    birthMonth,
    age,
    memberSince,
    terms: { version, acceptedAt },
  };
}
